import { BundleUsage, PaymentType } from "@/pages/users/types/user";
import { BundleUsageStatusValue } from "@/pages/users/types/bundle-usage";
import { Session } from "@/pages/sessions/types/session";
import { ExceptionLog, ExceptionType } from "@/features/exception-logs";
import { RH_RESCHEDULE_LT24_REASON } from "@/utils/cancellation-reasons";

export interface RescheduleExceptionEntry {
  session: Session;
  bundleName: string;
  // "Cancelled" → cancelled with the matching cancel_reason;
  // "Unsuccessful" → session marked unsuccessful with the matching reason;
  // "No-show" → session flagged is_no_show.
  source: "Cancelled" | "Unsuccessful" | "No-show";
}

// Sessions whose cancel_reason or (when unsuccessful) unsuccessful_reason
// matches the given reason exactly — never any other reason.
function collectSessionsByReason(
  bundlesUsage: BundleUsage[],
  reason: string,
): RescheduleExceptionEntry[] {
  const entries: RescheduleExceptionEntry[] = [];
  for (const bundle of bundlesUsage || []) {
    for (const session of bundle.sessions || []) {
      const isCancelledMatch = session.cancel_reason === reason;
      const isUnsuccessfulMatch =
        !!session.is_unsuccessful && session.unsuccessful_reason === reason;

      if (isCancelledMatch || isUnsuccessfulMatch) {
        entries.push({
          session,
          bundleName: bundle.bundle?.name ?? "—",
          source: isUnsuccessfulMatch ? "Unsuccessful" : "Cancelled",
        });
      }
    }
  }
  // Most recent first.
  return entries.sort(
    (a, b) =>
      new Date(b.session.start_time).getTime() -
      new Date(a.session.start_time).getTime(),
  );
}

// Reschedule exceptions are no longer derived here by string-matching session
// reasons — the server writes an ExceptionLog (RESCHEDULE) row when a session
// is cancelled or marked unsuccessful with the "Reschedule exception" reason,
// and the summary counts those via calculateExceptionCounts.

// RocketHealth-initiated reschedules within 24 hours of the session — sessions
// cancelled (or marked unsuccessful) with the exact "Reschedule by
// professional <24 hours" reason. Deliberately reason-string only: the derived
// Calendly signal (canceler_type 'host') also covers care-team/CRM cancels and
// would over-count.
export function collectRhReschedules(
  bundlesUsage: BundleUsage[],
): RescheduleExceptionEntry[] {
  return collectSessionsByReason(bundlesUsage, RH_RESCHEDULE_LT24_REASON);
}

export interface NoShowGroup {
  productName: string;
  entries: RescheduleExceptionEntry[];
}

// No-show sessions grouped by product (bundle name for older bundles without
// a product), largest count first; entries most recent first. Cancelled
// sessions still flagged is_no_show count — the flag records the missed
// occasion regardless of what happened to the booking afterwards.
export function collectNoShowsByProduct(
  bundlesUsage: BundleUsage[],
): NoShowGroup[] {
  const byProduct = new Map<string, RescheduleExceptionEntry[]>();
  for (const bundle of bundlesUsage || []) {
    for (const session of bundle.sessions || []) {
      if (!session.is_no_show) continue;
      // Same product-line grouping as buildProductSummaries.
      const productName =
        bundle.bundle?.product?.sub_category?.trim() ||
        (bundle.bundle?.product?.name ?? bundle.bundle?.name ?? "—");
      const entry: RescheduleExceptionEntry = {
        session,
        bundleName: bundle.bundle?.name ?? "—",
        source: "No-show",
      };
      const group = byProduct.get(productName);
      if (group) {
        group.push(entry);
      } else {
        byProduct.set(productName, [entry]);
      }
    }
  }
  return [...byProduct.entries()]
    .map(([productName, entries]) => ({
      productName,
      entries: entries.sort(
        (a, b) =>
          new Date(b.session.start_time).getTime() -
          new Date(a.session.start_time).getTime(),
      ),
    }))
    .sort((a, b) => b.entries.length - a.entries.length);
}

export interface RhRescheduleGroup {
  professionalName: string;
  entries: RescheduleExceptionEntry[];
}

// Groups RH-initiated reschedules by the professional whose session was moved,
// largest count first.
export function groupRhReschedulesByProfessional(
  entries: RescheduleExceptionEntry[],
): RhRescheduleGroup[] {
  const byName = new Map<string, RescheduleExceptionEntry[]>();
  for (const entry of entries) {
    const name = entry.session.professional?.name ?? "Unknown professional";
    const group = byName.get(name);
    if (group) {
      group.push(entry);
    } else {
      byName.set(name, [entry]);
    }
  }
  return [...byName.entries()]
    .map(([professionalName, groupEntries]) => ({
      professionalName,
      entries: groupEntries,
    }))
    .sort((a, b) => b.entries.length - a.entries.length);
}

/**
 * Collects every exception relevant to a user: their own user-linked logs plus
 * any logs attached to bundles they belong to (deduped by log id). This surfaces
 * "associated bundle exceptions" — e.g. for a couples bundle an exception is
 * logged against the primary user yet attached to the shared bundle, so the
 * partner's profile would otherwise miss it.
 */
export function collectUserExceptionLogs(
  userLogs: ExceptionLog[] = [],
  bundlesUsage: BundleUsage[] = [],
): ExceptionLog[] {
  const byId = new Map<string, ExceptionLog>();
  for (const log of userLogs) {
    byId.set(log.id, log);
  }
  for (const bundle of bundlesUsage) {
    for (const log of bundle.exceptionLogs ?? []) {
      if (!byId.has(log.id)) {
        byId.set(log.id, log);
      }
    }
  }
  return Array.from(byId.values());
}

/**
 * For an EXTRA_SESSION log the meaningful figure is how many sessions were
 * added (new_value.total_session_quantity - old_value.total_session_quantity),
 * not the existence of the log. Returns the positive delta, or 0 when the log
 * represents a reduction/no-op or is missing usable values.
 */
function extraSessionDelta(log: ExceptionLog): number {
  const oldCount = Number(log.old_value?.total_session_quantity);
  const newCount = Number(log.new_value?.total_session_quantity);
  if (!Number.isFinite(oldCount) || !Number.isFinite(newCount)) return 0;
  const delta = newCount - oldCount;
  return delta > 0 ? delta : 0;
}

export interface ExceptionCounts {
  counts: Partial<Record<ExceptionType, number>>;
  logsByType: Partial<Record<ExceptionType, ExceptionLog[]>>;
}

/**
 * Counts exceptions per type and keeps the underlying logs grouped alongside
 * (for tooltips showing which bundles/sessions each count refers to).
 * EXTRA_SESSION counts the summed sessions added (see extraSessionDelta);
 * every other type counts the number of logs.
 */
export function calculateExceptionCounts(
  exceptionLogs: ExceptionLog[] = [],
): ExceptionCounts {
  return exceptionLogs.reduce<ExceptionCounts>(
    (acc, log) => {
      const increment =
        log.exception_type === ExceptionType.EXTRA_SESSION
          ? extraSessionDelta(log)
          : 1;
      acc.counts[log.exception_type] =
        (acc.counts[log.exception_type] ?? 0) + increment;
      (acc.logsByType[log.exception_type] ??= []).push(log);
      return acc;
    },
    { counts: {}, logsByType: {} },
  );
}

// Statuses whose sessions no longer belong to this user: the pack's money was
// returned (FULLY_REFUNDED), its sessions moved to another user (TRANSFERRED),
// or it became another pack that has its own usage row (CONVERTED — counting
// both would double-count). These must not contribute to used/total session
// counts; the value is the badge label shown in place of the count.
//
// Keyed off BundleUsageStatusValue (not bare string) so a server-side rename of
// any of these statuses fails the build here rather than quietly making the
// pack count again — the client has no test runner to catch that otherwise.
export const NON_COUNTING_USAGE_STATUSES: Partial<
  Record<BundleUsageStatusValue, string>
> = {
  FULLY_REFUNDED: "Refunded",
  TRANSFERRED: "Transferred",
  CONVERTED: "Converted",
};

export function usageCountsTowardSessions(usage: BundleUsage): boolean {
  // hasOwnProperty, not `in` — `in` would also match Object.prototype keys.
  return !Object.prototype.hasOwnProperty.call(
    NON_COUNTING_USAGE_STATUSES,
    usage.status,
  );
}

export function calculateSessionsSummary(bundlesUsage: BundleUsage[]) {
  return bundlesUsage.reduce(
    (acc, bundle) => {
      if (!usageCountsTowardSessions(bundle)) return acc;
      acc.total += bundle.total_session_quantity;
      acc.used += bundle.utilized_session_quantity;
      return acc;
    },
    { total: 0, used: 0 }
  );
}

export interface UsagePaymentEntry {
  type: PaymentType;
  currency: string;
  // Minor units (paise). Net subtracts any refund — amount_refunded equals
  // amount for a full refund, so a fully refunded payment nets to 0.
  amount: number;
  amountRefunded: number;
  net: number;
  refundStatus: string | null;
  refundInitiatedAt: string | null;
}

/**
 * Every payment collected against a usage: the pack purchase
 * (usage.payment) plus per-session fees (session.fee_payments — reschedule /
 * no-show / late-cancel, already server-filtered to captured/refunded rows).
 *
 * Legacy paid reschedules (pre linked_session_id) were recorded as a child
 * usage of a bundle with type 'reschedule' — its payment IS the fee, so it's
 * labeled RESCHEDULE_FEE, not PURCHASE. Count-wise nothing changes: those
 * rows have no linked_session_id, so they only ever arrive through this slot.
 */
export function collectUsagePayments(usage: BundleUsage): UsagePaymentEntry[] {
  const entries: UsagePaymentEntry[] = [];
  const push = (
    type: PaymentType,
    payment: {
      amount: number;
      currency: string;
      amount_refunded?: number | null;
      refund_status?: string | null;
      refund_created_at?: string | null;
    },
  ) => {
    const amountRefunded = payment.amount_refunded ?? 0;
    entries.push({
      type,
      currency: payment.currency,
      amount: payment.amount,
      amountRefunded,
      net: payment.amount - amountRefunded,
      refundStatus: payment.refund_status ?? null,
      refundInitiatedAt: payment.refund_created_at ?? null,
    });
  };

  if (usage.payment) {
    push(
      usage.bundle?.type === "reschedule" ? "RESCHEDULE_FEE" : "PURCHASE",
      usage.payment,
    );
  }
  for (const session of usage.sessions || []) {
    for (const fee of session.fee_payments || []) {
      push(fee.type, fee);
    }
  }
  return entries;
}

export function calculatePaymentSummary(bundlesUsage: BundleUsage[]) {
  return bundlesUsage.reduce(
    (acc, bundle) => {
      // Purchase + session fees alike — the total is everything the user has
      // actually paid on this pack, net of refunds, converted to main units.
      for (const entry of collectUsagePayments(bundle)) {
        acc[entry.currency] = (acc[entry.currency] ?? 0) + entry.net / 100;
      }
      return acc;
    },
    {} as Record<string, number>
  );
}

export function groupBundlesByName(bundlesUsage: BundleUsage[]) {
  return bundlesUsage.reduce(
    (acc, bundleUsage) => {
      const bundleName = bundleUsage.bundle.name;
      if (!acc[bundleName]) {
        acc[bundleName] = [];
      }
      acc[bundleName].push(bundleUsage);
      return acc;
    },
    {} as Record<string, BundleUsage[]>
  );
}

export function findClosestToExpiry(bundlesUsage: BundleUsage[]) {
  const today = new Date();
  const bundlesWithRemainingSessions = bundlesUsage
    .filter(bundle => {
      const remaining = bundle.total_session_quantity - bundle.utilized_session_quantity;
      return remaining > 0 && bundle.status === 'ACTIVE';
    })
    .map(bundle => {
      const endDate = new Date(bundle.end_date);
      const daysUntilExpiry = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      const remaining = bundle.total_session_quantity - bundle.utilized_session_quantity;
      return {
        ...bundle,
        daysUntilExpiry,
        remaining,
      };
    })
    .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);

  return bundlesWithRemainingSessions[0] || null;
}

// Tier by total sessions purchased. Only the lower bound matters — each tier's
// upper bound is the next tier's floor. Add/reorder a tier = one row.
const TIERS: [min: number, label: string][] = [
  [12, "Diamond"],
  [8, "Platinum"],
  [4, "Gold"],
  [2, "Silver"],
  [1, "Single"],
];

export function computeTierTag(bundlesUsage: BundleUsage[]) {
  // Each bundle's sessions are weighted by (1 - fraction refunded): a fully
  // refunded bundle contributes 0, a 20%-refunded bundle contributes 0.8× its
  // sessions. Sum, then round to the nearest whole session for the threshold.
  const totalPurchased = bundlesUsage.reduce((acc, b) => {
    const sessions = b.total_session_quantity || 0;
    const amount = b.payment?.amount ?? 0;
    const refunded = b.payment?.amount_refunded ?? 0;
    const refundFraction = amount > 0 ? Math.min(refunded / amount, 1) : 0;
    return acc + sessions * (1 - refundFraction);
  }, 0);

  const rounded = Math.round(totalPurchased);
  return TIERS.find(([min]) => rounded >= min)?.[1] ?? null;
}

function getLastSessionDate(bundlesUsage: BundleUsage[]) {
  const dates: number[] = [];
  for (const usage of bundlesUsage) {
    for (const s of usage.sessions || []) {
      const start = (s as any).start_time;
      if (!start) continue;
      const t = new Date(start).getTime();
      if (!Number.isNaN(t)) dates.push(t);
    }
  }
  if (dates.length === 0) return null;
  return new Date(Math.max(...dates));
}

export function computeJourneyTag(bundlesUsage: BundleUsage[]) {
  const utilized = bundlesUsage.reduce(
    (acc, b) => acc + (b.utilized_session_quantity || 0),
    0
  );

  const hasActiveBundle = bundlesUsage.some((b) => b.status === "ACTIVE");
  const lastSession = getLastSessionDate(bundlesUsage);

  if (lastSession) {
    const daysInactive = Math.floor(
      (Date.now() - lastSession.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysInactive >= 60) {
      return hasActiveBundle ? "Dormant" : "Drop Off";
    }
  }
  if (utilized >= 11) return "Deep Journey";
  if (utilized >= 4) return "Mid Journey";
  if (utilized >= 1) return "Early Journey";
  return null;
}

export function computeTransitionTag(count?: number | null) {
  const c = count ?? 0;
  if (c <= 0) return null;
  if (c === 1) return "Transition by RH - Once";
  if (c === 2) return "Transition by RH - Twice";
  return "Transition by RH - Multiple times";
}
